import { createContext, useContext } from 'react';

/** The draft Lumio ended up creating, for the reply that announces it. */
export interface LumioArticleResult {
  id: string;
  title: string;
}

export interface LumioArticleControl {
  /**
   * Have Lumio write a blog article about `topic` and create it as a draft.
   * Resolves with the created article, rejects if the writing or the save
   * failed. The blog list shows a generating row for the whole call.
   *
   * `null` when no host provides the capability (the chat panel mounted
   * standalone, e.g. in its Playwright CT): the chat then treats an article
   * request as an ordinary question and sends it to the assistant.
   */
  writeArticle: ((topic: string) => Promise<LumioArticleResult>) | null;
  /** A job is already running. One at a time, so a second ask cannot open a
   *  second placeholder row. */
  writing: boolean;
}

/**
 * The "Lumio, write me an article" bus, provided by `MainLayout` (which has the
 * store and the router) and consumed by the Lumio surfaces. Deliberately
 * separate from `LumioControlContext`: that one is the page → Lumio direction
 * ("ask Lumio this"), this is Lumio → studio ("do this in the app"). The
 * default is inert so the Lumio components still mount with no provider.
 */
export const LumioArticleContext = createContext<LumioArticleControl>({
  writeArticle: null,
  writing: false,
});

/** Access the article-writing capability from under `MainLayout`. */
export const useLumioArticle = (): LumioArticleControl => useContext(LumioArticleContext);
